import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Plus, X, MessageSquare, CalendarPlus, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Extended group panel: Charter, Meetings & Attendance, and in-group Chat.
 * Mounted inside the group detail dialog on GroupsPage.
 */
const GroupExtrasPanel = ({ group }) => {
  const [tab, setTab] = useState('charter');
  if (!group) return null;
  return (
    <div className="mt-4 border-t pt-4" data-testid="group-extras">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-3">
          <TabsTrigger value="charter" data-testid="tab-charter"><ClipboardList className="w-3 h-3 mr-1" />Charter</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings"><CalendarPlus className="w-3 h-3 mr-1" />Meetings &amp; Attendance</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="w-3 h-3 mr-1" />Chat</TabsTrigger>
        </TabsList>
        <TabsContent value="charter"><CharterTab group={group} /></TabsContent>
        <TabsContent value="meetings"><MeetingsTab group={group} /></TabsContent>
        <TabsContent value="chat"><ChatTab group={group} /></TabsContent>
      </Tabs>
    </div>
  );
};

const CharterTab = ({ group }) => {
  const [form, setForm] = useState({ mission: '', goals: [], constitution: '' });
  const [newGoal, setNewGoal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/groups/${group.id}/charter`); setForm({ mission: r.data.mission || '', goals: r.data.goals || [], constitution: r.data.constitution || '' }); }
    catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [group.id]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/groups/${group.id}/charter`, form); toast.success('Charter saved'); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addGoal = () => { if (newGoal.trim()) { setForm({ ...form, goals: [...form.goals, newGoal.trim()] }); setNewGoal(''); } };
  const removeGoal = (i) => setForm({ ...form, goals: form.goals.filter((_, idx) => idx !== i) });

  if (loading) return <div className="text-center py-4 text-gray-400 text-xs">Loading...</div>;
  return (
    <div className="space-y-3" data-testid="charter-tab">
      <div>
        <Label className="text-xs">Mission statement</Label>
        <textarea value={form.mission} onChange={e => setForm({ ...form, mission: e.target.value })} rows={2} maxLength={2000} className="w-full border border-gray-200 p-2 text-sm" placeholder="Why does this group exist?" data-testid="charter-mission" />
      </div>
      <div>
        <Label className="text-xs">Goals</Label>
        <div className="flex gap-2 mb-2">
          <Input value={newGoal} onChange={e => setNewGoal(e.target.value)} placeholder="Add a goal…" onKeyDown={e => e.key === 'Enter' && addGoal()} className="text-xs" data-testid="charter-new-goal" />
          <Button size="sm" variant="outline" onClick={addGoal} data-testid="charter-add-goal"><Plus className="w-3 h-3" /></Button>
        </div>
        <ul className="space-y-1">
          {form.goals.map((g, i) => (
            <li key={i} className="flex items-center justify-between bg-gray-50 px-3 py-1.5 text-xs border border-gray-100" data-testid={`charter-goal-${i}`}>
              <span>• {g}</span>
              <button onClick={() => removeGoal(i)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
            </li>
          ))}
          {form.goals.length === 0 && <li className="text-xs text-gray-400">No goals defined.</li>}
        </ul>
      </div>
      <div>
        <Label className="text-xs">Constitution / By-laws</Label>
        <textarea value={form.constitution} onChange={e => setForm({ ...form, constitution: e.target.value })} rows={6} maxLength={20000} className="w-full border border-gray-200 p-2 text-xs font-mono" placeholder="Meeting rules, quorum, voting, dissolution, etc." data-testid="charter-constitution" />
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#1f1f1f] text-white" data-testid="charter-save">{saving ? 'Saving…' : 'Save Charter'}</Button>
    </div>
  );
};

const MeetingsTab = ({ group }) => {
  const [meetings, setMeetings] = useState([]);
  const [form, setForm] = useState({ meeting_date: '', location: group.location || '', agenda: [], notes: '' });
  const [newAgenda, setNewAgenda] = useState('');
  const [summary, setSummary] = useState(null);
  const [attMeeting, setAttMeeting] = useState(null); // meeting being marked

  const loadMeetings = async () => {
    try { const r = await api.get(`/groups/${group.id}/meetings`); setMeetings(r.data); }
    catch {}
  };
  const loadSummary = async () => {
    try { const r = await api.get(`/groups/${group.id}/attendance-summary`); setSummary(r.data); }
    catch {}
  };
  useEffect(() => { loadMeetings(); loadSummary(); }, [group.id]);

  const create = async () => {
    if (!form.meeting_date) return toast.error('Meeting date required');
    try { await api.post(`/groups/${group.id}/meetings`, form); toast.success('Meeting created'); setForm({ meeting_date: '', location: group.location || '', agenda: [], notes: '' }); loadMeetings(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const togglePresent = (clientId) => {
    if (!attMeeting) return;
    const p = new Set(attMeeting._present || []);
    p.has(clientId) ? p.delete(clientId) : p.add(clientId);
    setAttMeeting({ ...attMeeting, _present: Array.from(p) });
  };
  const submitAttendance = async () => {
    const allIds = (group.members || []).map(m => m.client_id);
    const present = attMeeting._present || [];
    const absent = allIds.filter(id => !present.includes(id));
    try {
      await api.post(`/groups/${group.id}/meetings/${attMeeting.id}/attendance`, { present_client_ids: present, absent_client_ids: absent });
      toast.success(`Attendance saved: ${present.length} present, ${absent.length} absent`);
      setAttMeeting(null); loadMeetings(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="meetings-tab">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Schedule Meeting</h4>
        <div className="space-y-2 bg-gray-50 border p-3">
          <Input type="date" value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} data-testid="meet-date" />
          <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Location" data-testid="meet-location" />
          <div className="flex gap-2">
            <Input value={newAgenda} onChange={e => setNewAgenda(e.target.value)} placeholder="Agenda item" className="text-xs" onKeyDown={e => e.key === 'Enter' && newAgenda && (setForm({ ...form, agenda: [...form.agenda, newAgenda] }), setNewAgenda(''))} />
            <Button size="sm" variant="outline" onClick={() => { if (newAgenda.trim()) { setForm({ ...form, agenda: [...form.agenda, newAgenda.trim()] }); setNewAgenda(''); } }}><Plus className="w-3 h-3" /></Button>
          </div>
          {form.agenda.map((a, i) => <div key={i} className="flex justify-between text-[11px] bg-white px-2 py-1 border"><span>{i + 1}. {a}</span><button onClick={() => setForm({ ...form, agenda: form.agenda.filter((_, j) => j !== i) })} className="text-red-400"><X className="w-3 h-3" /></button></div>)}
          <Button onClick={create} size="sm" className="bg-[#1f1f1f] text-white w-full" data-testid="meet-create">Create Meeting</Button>
        </div>

        {attMeeting && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200" data-testid="attendance-form">
            <p className="text-xs font-semibold text-amber-800 mb-2">Mark attendance — {attMeeting.meeting_date}</p>
            <div className="max-h-40 overflow-y-auto border bg-white">
              {(group.members || []).map(m => (
                <label key={m.client_id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={(attMeeting._present || []).includes(m.client_id)} onChange={() => togglePresent(m.client_id)} data-testid={`att-check-${m.client_id}`} />
                  <span>{m.client_name}</span>
                </label>
              ))}
              {(!group.members || group.members.length === 0) && <p className="px-2 py-2 text-gray-400 text-xs">No members.</p>}
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={submitAttendance} className="bg-emerald-600 text-white" data-testid="att-submit">Save Attendance</Button>
              <Button size="sm" variant="outline" onClick={() => setAttMeeting(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Past Meetings</h4>
        <div className="border border-gray-200 divide-y max-h-60 overflow-y-auto" data-testid="meetings-list">
          {meetings.length === 0 && <p className="text-center text-gray-400 text-xs py-4">No meetings yet.</p>}
          {meetings.map(m => (
            <div key={m.id} className="p-2 text-xs flex justify-between items-center" data-testid={`meeting-row-${m.id}`}>
              <div>
                <div className="font-medium">{m.meeting_date} — {m.location || 'TBD'}</div>
                <div className="text-[10px] text-gray-400 uppercase">{m.status} · {m.attendees_ids?.length || 0} present, {m.absentees_ids?.length || 0} absent</div>
              </div>
              {m.status !== 'completed' && <Button size="sm" variant="outline" onClick={() => setAttMeeting({ ...m, _present: m.attendees_ids || [] })} className="h-7 text-xs" data-testid={`take-attendance-${m.id}`}>Take attendance</Button>}
            </div>
          ))}
        </div>

        {summary && summary.total_completed_meetings > 0 && (
          <div className="mt-3" data-testid="attendance-summary">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Attendance Rate ({summary.total_completed_meetings} meetings)</h4>
            <table className="w-full text-[11px] border">
              <thead className="bg-gray-50"><tr><th className="text-left p-1">Member</th><th className="p-1">Present</th><th className="p-1">Absent</th><th className="p-1">Rate</th></tr></thead>
              <tbody>{summary.members.map(r => <tr key={r.client_id} className="border-t"><td className="p-1">{r.client_name}</td><td className="p-1 text-center">{r.present}</td><td className="p-1 text-center">{r.absent}</td><td className={`p-1 text-center font-bold ${r.attendance_rate >= 75 ? 'text-emerald-600' : r.attendance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{r.attendance_rate}%</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ChatTab = ({ group }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sinceToken, setSinceToken] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/groups/${group.id}/chat?limit=100`);
      setMessages(r.data.messages || []);
      setSinceToken(r.data.server_time);
    } catch {}
  };
  useEffect(() => {
    load();
    const interval = setInterval(async () => {
      if (!sinceToken) return;
      try {
        const r = await api.get(`/groups/${group.id}/chat?since=${encodeURIComponent(sinceToken)}`);
        if (r.data.messages?.length) setMessages(prev => [...prev, ...r.data.messages]);
        setSinceToken(r.data.server_time);
      } catch {}
    }, 7000);
    return () => clearInterval(interval);
  }, [group.id]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const cid = `web-${Date.now()}`;
      await api.post(`/groups/${group.id}/chat`, { text: text.trim(), client_message_id: cid });
      setText('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-96" data-testid="chat-tab">
      <div className="flex-1 overflow-y-auto border border-gray-200 p-3 bg-gray-50 space-y-2" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-center text-xs text-gray-400 py-4">No messages yet. Be the first to say hi.</p>}
        {messages.map(m => (
          <div key={m.id} className="bg-white border border-gray-200 px-3 py-2" data-testid={`chat-msg-${m.id}`}>
            <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
              <span><strong className="text-[#1f1f1f]">{m.sender_name}</strong> <span className="uppercase text-gray-400">({m.sender_role})</span></span>
              <span>{m.created_at?.slice(11, 16)}</span>
            </div>
            <div className="text-sm whitespace-pre-wrap">{m.text}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()} placeholder="Type a message…" data-testid="chat-input" />
        <Button onClick={send} disabled={sending} className="bg-[#1f1f1f] text-white" data-testid="chat-send">{sending ? '…' : 'Send'}</Button>
      </div>
      <p className="text-[10px] text-gray-400 mt-1">Messages sync every 7s. On mobile use <code>GET /api/groups/{'{id}'}/chat?since=&lt;ISO&gt;</code> for offline polling.</p>
    </div>
  );
};

export default GroupExtrasPanel;
